/**
 * The games you last played, kept with your account.
 *
 * Storage shared with favourites - see `net/player-record.ts` for why it is
 * neither an RPC nor localStorage.
 *
 * ## What counts as playing
 *
 * A deal, not a door. Opening a table and backing out of it is not playing,
 * and a list that filled up with rooms somebody glanced at would be a worse
 * answer to "what was I playing" than no list at all. `remember` is called
 * when a room reaches `playing`, which is the moment cards exist.
 *
 * ## Most recent first, and only once
 *
 * See `render/recent-list.ts`, which holds the rule and its tests.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { readGameIds, writeGameIds } from './net/player-record.js';
import { useSession } from './session-context.js';
import { promote } from './render/recent-list.js';

const KEY = 'recent';

export interface Recent {
  /** Game ids, most recently played first. */
  readonly ids: readonly string[];
  /** Note that a game has been played. Safe to call repeatedly. */
  readonly remember: (gameId: string) => void;
  readonly loading: boolean;
}

export function useRecent(): Recent {
  const { session } = useSession();
  const [ids, setIds] = useState<readonly string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) {
      setIds([]);
      setLoading(false);
      return;
    }

    let live = true;
    void (async () => {
      const stored = await readGameIds(session, KEY);
      if (live) {
        setIds(stored);
        setLoading(false);
      }
    })();

    return () => {
      live = false;
    };
  }, [session]);

  const latest = useRef(ids);
  latest.current = ids;

  /*
   * Written only when it would change something.
   *
   * `remember` is called from a render-driven effect, so it runs again on
   * every packet the table receives - a write per packet would be hundreds a
   * game. A game already at the front is already the answer.
   */
  const remember = useCallback(
    (gameId: string) => {
      if (!session || gameId === '') return;
      if (latest.current[0] === gameId) return;

      const after = promote(latest.current, gameId);
      const before = latest.current;
      setIds(after);
      void writeGameIds(session, KEY, after).catch(() => {
        setIds(before);
      });
    },
    [session],
  );

  return { ids, remember, loading };
}
